import React, { useMemo, useState } from "react";
import { Box, Button, Checkbox, Divider, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import ModalShell from "./ModalShell";
import FileDropzone from "./FileDropzone";
import { readSurveyYamlDetails } from "./surveyYaml";
import { CreateCollectionDraft, VariantDraft } from "./types";
import {
  captionLabelSx,
  collectionColors,
  fieldLabelSx,
  font,
  inputSx,
  outlinedButtonSx,
  subtitleSx,
} from "./tokens";

interface CreateCollectionModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (draft: CreateCollectionDraft) => void;
  isSubmitting?: boolean;
}

let variantKeySeed = 0;
const nextVariantKey = () => {
  variantKeySeed += 1;
  return `variant-${variantKeySeed}`;
};

const CreateCollectionModal: React.FC<CreateCollectionModalProps> = ({
  open,
  onClose,
  onSubmit,
  isSubmitting = false,
}) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [importFromFile, setImportFromFile] = useState(false);
  const [primaryLabel, setPrimaryLabel] = useState("");
  const [primaryFile, setPrimaryFile] = useState<File | null>(null);
  const [additional, setAdditional] = useState<VariantDraft[]>([]);
  // Set when the import found no title in the file. The checkbox promises to fill the
  // fields in; when it cannot, saying so beats leaving them blank next to a Create
  // button that refuses to enable for reasons the researcher cannot see.
  const [importFoundNothing, setImportFoundNothing] = useState(false);

  const reset = () => {
    setName("");
    setDescription("");
    setImportFromFile(false);
    setImportFoundNothing(false);
    setPrimaryLabel("");
    setPrimaryFile(null);
    setAdditional([]);
  };

  const close = () => {
    reset();
    onClose();
  };

  const applyImportedDetails = async (file: File) => {
    const details = await readSurveyYamlDetails(file);
    if (details.title) {
      setName(details.title);
    }
    if (details.description) {
      setDescription(details.description);
    }
    // The title is what the Create button waits on, so that is what decides whether the
    // import delivered. A file with a title and no description is a success.
    setImportFoundNothing(!details.title);
  };

  const handlePrimaryFile = (file: File | null) => {
    setPrimaryFile(file);
    if (file && importFromFile) {
      void applyImportedDetails(file);
    }
  };

  const handleToggleImport = (checked: boolean) => {
    setImportFromFile(checked);
    if (!checked) {
      setImportFoundNothing(false);
      return;
    }
    if (primaryFile) {
      void applyImportedDetails(primaryFile);
    }
  };

  const duplicateLabel = useMemo(() => {
    const labels = [primaryLabel, ...additional.map((variant) => variant.label)]
      .map((label) => label.trim().toLowerCase())
      .filter(Boolean);
    return labels.length !== new Set(labels).size;
  }, [primaryLabel, additional]);

  const additionalComplete = additional.every((variant) => variant.label.trim() && variant.file);
  const canSubmit =
    Boolean(name.trim()) &&
    Boolean(primaryLabel.trim()) &&
    Boolean(primaryFile) &&
    additionalComplete &&
    !duplicateLabel;

  const handleConfirm = () => {
    if (!canSubmit || !primaryFile) {
      return;
    }
    onSubmit({
      name: name.trim(),
      description: description.trim(),
      primary: { label: primaryLabel.trim(), file: primaryFile },
      additional: additional.map((variant) => ({
        label: variant.label.trim(),
        file: variant.file as File,
      })),
    });
  };

  return (
    <ModalShell
      open={open}
      onClose={close}
      title="Create Collection"
      subtitle="Set up a new collection with a primary survey variant and optional additional variants."
      width={578}
      confirmLabel="Create Collection"
      onConfirm={handleConfirm}
      confirmDisabled={!canSubmit}
      isSubmitting={isSubmitting}
    >
      {/* Import from YAML */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "4px", marginLeft: "-9px" }}>
        <Checkbox
          checked={importFromFile}
          onChange={(event) => handleToggleImport(event.target.checked)}
          size="small"
          sx={{ color: collectionColors.textSecondary, "&.Mui-checked": { color: collectionColors.buttonPrimary } }}
        />
        <Typography sx={{ ...fieldLabelSx, fontWeight: 400 }}>
          Import title and description from the survey file
        </Typography>
      </Box>

      {importFromFile && importFoundNothing && (
        <Typography sx={{ ...subtitleSx, color: collectionColors.danger }}>
          This file carries no title — enter one below.
        </Typography>
      )}

      {/* Title */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <Typography sx={fieldLabelSx}>
          Title <Box component="span" sx={{ color: collectionColors.danger }}>*</Box>
        </Typography>
        <Box
          component="input"
          type="text"
          placeholder="e.g. Health Assessment 2024"
          value={name}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => setName(event.target.value)}
          sx={inputSx}
        />
      </Box>

      {/* Description */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <Typography sx={fieldLabelSx}>Description</Typography>
        <Box
          component="textarea"
          rows={3}
          placeholder="Brief description of this collection..."
          value={description}
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => setDescription(event.target.value)}
          sx={{ ...inputSx, height: "auto", padding: "10px 12px", resize: "vertical", fontFamily: font }}
        />
      </Box>

      <Divider sx={{ borderColor: collectionColors.border }} />

      {/* Primary variant */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <Typography sx={fieldLabelSx}>
          Primary Survey Variant <Box component="span" sx={{ color: collectionColors.danger }}>*</Box>
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "12px",
            padding: "16px",
            border: `1px solid ${collectionColors.border}`,
            borderRadius: "10px",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <Typography sx={captionLabelSx}>Label</Typography>
            <Box
              component="input"
              type="text"
              placeholder="e.g. English"
              value={primaryLabel}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setPrimaryLabel(event.target.value)}
              sx={inputSx}
            />
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <Typography sx={captionLabelSx}>YAML file</Typography>
            <FileDropzone
              file={primaryFile}
              onSelect={handlePrimaryFile}
              placeholder="Upload primary .yaml / .yml"
              compact
            />
          </Box>
        </Box>
      </Box>

      {/* Additional variants */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <Typography sx={fieldLabelSx}>Additional Variants</Typography>
        <Typography sx={{ ...subtitleSx, fontSize: "13px", lineHeight: "18px" }}>
          Optional — variants can also be added later. Labels are free text, so they can describe a
          panel or setting instead of a language.
        </Typography>

        {additional.map((variant, index) => (
          <Box
            key={variant.key}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "12px",
              padding: "16px",
              border: `1px solid ${collectionColors.border}`,
              borderRadius: "10px",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Typography sx={captionLabelSx}>Variant {index + 2}</Typography>
              <Box
                component="button"
                type="button"
                aria-label="Remove variant"
                onClick={() => setAdditional((prev) => prev.filter((item) => item.key !== variant.key))}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "24px",
                  height: "24px",
                  padding: 0,
                  background: "transparent",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  "&:hover": { background: collectionColors.dangerSurface },
                }}
              >
                <CloseIcon sx={{ width: 14, height: 14, color: collectionColors.textSecondary }} />
              </Box>
            </Box>
            <Box
              component="input"
              type="text"
              placeholder="Label — e.g. Spanish, Panel B"
              value={variant.label}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setAdditional((prev) =>
                  prev.map((item) => (item.key === variant.key ? { ...item, label: event.target.value } : item))
                )
              }
              sx={inputSx}
            />
            <FileDropzone
              file={variant.file}
              onSelect={(file) =>
                setAdditional((prev) => prev.map((item) => (item.key === variant.key ? { ...item, file } : item)))
              }
              placeholder="Upload .yaml / .yml"
              compact
            />
          </Box>
        ))}

        <Button
          fullWidth
          startIcon={<AddIcon sx={{ width: 16, height: 16 }} />}
          onClick={() => setAdditional((prev) => [...prev, { key: nextVariantKey(), label: "", file: null }])}
          sx={{ ...outlinedButtonSx, height: "36px" }}
        >
          Add Variant
        </Button>
      </Box>

      {duplicateLabel && (
        <Typography sx={{ ...subtitleSx, color: collectionColors.danger }}>
          Variant labels must be unique within a collection.
        </Typography>
      )}
    </ModalShell>
  );
};

export default CreateCollectionModal;
